import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const SignaturesTab = ({ contractId, onUpdate }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedSignatory, setSelectedSignatory] = useState(null);
  const [newSignatory, setNewSignatory] = useState({
    name: '',
    email: '',
    role: '',
    company: ''
  });

  const signatories = [
    {
      id: 1,
      name: "John Mitchell",
      email: "john.mitchell@techcorp.com",
      role: "Chief Technology Officer",
      company: "TechCorp Solutions",
      status: "signed",
      signedDate: "2024-01-15T10:30:00Z",
      ipAddress: "192.168.1.100"
    },
    {
      id: 2,
      name: "Sarah Johnson",
      email: "sarah.johnson@innovateservices.com",
      role: "Contract Manager",
      company: "Innovate Services LLC",
      status: "pending",
      sentDate: "2024-01-10T14:20:00Z",
      remindersSent: 2
    },
    {
      id: 3,
      name: "Michael Rodriguez",
      email: "m.rodriguez@legalfirm.com",
      role: "Legal Counsel",
      company: "Rodriguez & Associates",
      status: "viewed",
      viewedDate: "2024-01-12T09:15:00Z"
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'signed': return 'bg-success/10 text-success border-success/20';
      case 'pending': return 'bg-warning/10 text-warning border-warning/20';
      case 'viewed': return 'bg-accent/10 text-accent border-accent/20';
      case 'declined': return 'bg-error/10 text-error border-error/20';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'signed': return 'CheckCircle';
      case 'pending': return 'Clock';
      case 'viewed': return 'Eye';
      case 'declined': return 'XCircle';
      default: return 'Circle';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleAddSignatory = () => {
    setSelectedSignatory(null);
    setNewSignatory({ name: '', email: '', role: '', company: '' });
    setIsDrawerOpen(true);
  };

  const handleEditSignatory = (signatory) => {
    setSelectedSignatory(signatory);
    setNewSignatory({
      name: signatory?.name,
      email: signatory?.email,
      role: signatory?.role,
      company: signatory?.company
    });
    setIsDrawerOpen(true);
  };

  const handleSaveSignatory = () => {
    console.log('Saving signatory:', newSignatory);
    setIsDrawerOpen(false);
    setSelectedSignatory(null);
    setNewSignatory({ name: '', email: '', role: '', company: '' });
  };

  const handleSendReminder = (signatory) => {
    console.log('Sending reminder to:', signatory?.email);
  };

  const handleResendInvitation = (signatory) => {
    console.log('Resending invitation to:', signatory?.email);
  };

  return (
    <div className="p-6 bg-background">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Signature Management</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Manage contract signatories and track signature status
            </p>
          </div>
          <Button
            variant="default"
            size="sm"
            iconName="UserPlus"
            iconPosition="left"
            onClick={handleAddSignatory}
          >
            Add Signatory
          </Button>
        </div>

        {/* Signature Progress */}
        <div className="bg-card p-6 rounded-lg border border-border mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-foreground">Signature Progress</h3>
            <div className="text-sm text-muted-foreground">
              2 of 3 signatures completed
            </div>
          </div>
          
          <div className="w-full bg-muted rounded-full h-2 mb-4">
            <div className="bg-primary h-2 rounded-full" style={{ width: '66.67%' }}></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-success/10 rounded-lg">
              <div className="text-2xl font-bold text-success">1</div>
              <div className="text-sm text-muted-foreground">Signed</div>
            </div>
            <div className="text-center p-4 bg-warning/10 rounded-lg">
              <div className="text-2xl font-bold text-warning">1</div>
              <div className="text-sm text-muted-foreground">Pending</div>
            </div>
            <div className="text-center p-4 bg-accent/10 rounded-lg">
              <div className="text-2xl font-bold text-accent">1</div>
              <div className="text-sm text-muted-foreground">Viewed</div>
            </div>
          </div>
        </div>

        {/* Signatories List */}
        <div className="bg-card rounded-lg border border-border">
          <div className="p-6 border-b border-border">
            <h3 className="text-lg font-medium text-foreground">Signatories</h3>
          </div>
          
          <div className="divide-y divide-border">
            {signatories?.map((signatory) => (
              <div key={signatory?.id} className="p-6 hover:bg-muted/30 transition-legal-fast">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-primary">
                        {signatory?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                      </span>
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="font-medium text-foreground">{signatory?.name}</h4>
                        <div className={`px-2 py-1 rounded-full text-xs font-medium border flex items-center space-x-1 ${getStatusColor(signatory?.status)}`}>
                          <Icon name={getStatusIcon(signatory?.status)} size={12} />
                          <span className="capitalize">{signatory?.status}</span>
                        </div>
                      </div>
                      
                      <div className="text-sm text-muted-foreground space-y-1">
                        <p>{signatory?.role} at {signatory?.company}</p>
                        <p>{signatory?.email}</p>
                        
                        {signatory?.status === 'signed' && (
                          <p className="text-success">
                            Signed on {formatDate(signatory?.signedDate)} from {signatory?.ipAddress}
                          </p>
                        )}
                        
                        {signatory?.status === 'pending' && (
                          <p className="text-warning">
                            Sent on {formatDate(signatory?.sentDate)} • {signatory?.remindersSent} reminders sent
                          </p>
                        )}
                        
                        {signatory?.status === 'viewed' && (
                          <p className="text-accent">
                            Viewed on {formatDate(signatory?.viewedDate)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {signatory?.status === 'pending' && (
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Send"
                        onClick={() => handleSendReminder(signatory)}
                      >
                        Remind
                      </Button>
                    )}
                    
                    {signatory?.status !== 'signed' && (
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="RotateCcw"
                        onClick={() => handleResendInvitation(signatory)}
                      >
                        Resend
                      </Button>
                    )}
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Edit3"
                      onClick={() => handleEditSignatory(signatory)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Side Drawer */}
      {isDrawerOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setIsDrawerOpen(false)}
          />
          
          <div className="fixed right-0 top-0 h-full w-96 bg-card border-l border-border shadow-legal-lg z-50 transform transition-transform">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">
                {selectedSignatory ? 'Edit Signatory' : 'Add Signatory'}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                iconName="X"
                onClick={() => setIsDrawerOpen(false)}
              />
            </div>
            
            <div className="p-6 space-y-4">
              <Input
                label="Full Name"
                type="text"
                value={newSignatory?.name}
                onChange={(e) => setNewSignatory(prev => ({ ...prev, name: e?.target?.value }))}
                placeholder="Enter full name"
                required
              />
              
              <Input
                label="Email Address"
                type="email"
                value={newSignatory?.email}
                onChange={(e) => setNewSignatory(prev => ({ ...prev, email: e?.target?.value }))}
                placeholder="Enter email address"
                required
              />
              
              <Input
                label="Role/Title"
                type="text"
                value={newSignatory?.role}
                onChange={(e) => setNewSignatory(prev => ({ ...prev, role: e?.target?.value }))}
                placeholder="Enter role or title"
                required
              />
              
              <Input
                label="Company"
                type="text"
                value={newSignatory?.company}
                onChange={(e) => setNewSignatory(prev => ({ ...prev, company: e?.target?.value }))}
                placeholder="Enter company name"
                required
              />
            </div>
            
            <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-border bg-muted/30">
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsDrawerOpen(false)}
                  fullWidth
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSaveSignatory}
                  fullWidth
                >
                  {selectedSignatory ? 'Update' : 'Add'} Signatory
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SignaturesTab;